import { notification } from 'antd';
import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';

export default function useImageSliderHook({ images, hotel }) {
  const { API } = useContext(ApiContext);
  const { appActions } = useContext(AppContext);
  const { translate, getSelectedHotel } = appActions;

  const [state, setState] = useState({
    previewOpen: false,
    previewImage: '',
    fileList: [],
    hotel: null,
  });

  useEffect(() => {
    updateState({ hotel: hotel });
    const formattedImages = parseImages(images);
    updateState({ fileList: formattedImages });
  }, [images]);

  const actions = {
    copyToHotels: params => {
      const { hotels } = params;
      if (!hotels || hotels.length === 0) return;
      updateState({ isLoading: true });
      Promise.all(
        hotels.map(hotel => {
          if (hotel.hotelId !== state.hotel.id) {
            API.appearenceRequest(hotel.hotelId)
              .then(res => {
                const imagesRemove = parseImages(res.sliderImages);
                if (imagesRemove.length !== 0) {
                  imagesRemove.map(image => {
                    actions.removeImage(image, hotel.hotelId);
                  });
                }
                state.fileList.map((file, index) => {
                  actions.onImageAdded(JSON.stringify(file), hotel.hotelId, index);
                });
              })
              .catch(error => {
                notification.error({
                  message: translate('appearence.imageError'),
                });
              });
          }
        })
      );
      updateState({ isLoading: false });
    },

    handlePreview: async file => {
      if (!file.url && !file.preview) {
        file.preview = await actions.getBase64(file.originFileObj);
      }

      updateState({
        previewImage: file.url || file.preview,
        previewOpen: true,
      });
    },

    getBase64: file =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      }),

    setPreviewOpen: open => updateState({ previewOpen: open }),

    setPreviewImage: img => updateState({ previewImage: img }),

    onImageAdded: (file, hotelID = null, amount = null) => {
      const hotelId = hotelID ? hotelID : getSelectedHotel()?.key;
      const currentAmount = amount ? amount : state.fileList.length;
      const updatedFileList = [
        ...state.fileList,
        {
          uid: file.uid,
          name: file.name,
          status: 'uploading',
          url: null,
        },
      ];

      if (!hotelID || hotelID === appActions.getSelectedHotel()?.key)
        updateState({ fileList: updatedFileList });
      API.uploadSliderImage({
        hotelId: hotelId,
        file,
        currentAmount: currentAmount,
      })
        .then(res => {
          const updatedFileList = [
            ...state.fileList,
            {
              uid: file.uid,
              name: file.name,
              status: 'done',
              url: res.path,
            },
          ];

          if (!hotelID || hotelID === appActions.getSelectedHotel()?.key)
            updateState({ fileList: updatedFileList });

          notification.success({
            message: translate('appearence.imageSaveSuccess'),
          });
        })
        .catch(error => {
          updateState({ isLoading: false });
          notification.error({
            message: translate('appearence.imageSaveError'),
          });
        });
    },

    removeImage: (data, hotelID = null) => {
      const hotelId = hotelID ? hotelID : getSelectedHotel()?.key;
      const imageToRemove = state.fileList.find(f => f.uid === data.uid);
      if (!hotelID || hotelID === appActions.getSelectedHotel()?.key)
        updateState({ fileList: state.fileList.filter(f => f.url !== data.url) });
      API.removeSliderImage({
        hotelId: hotelId,
        uid: hotelID ? data.uid : imageToRemove.uid,
        url: hotelID ? data.url : imageToRemove.url,
      })
        .then(_ => {
          notification.success({
            message: translate('appearence.imageRemoveSuccess'),
          });
          updateState({ isLoading: false });
        })
        .catch(error => {
          updateState({ isLoading: false });
          notification.error({
            message: translate('appearence.imageRemoveError'),
          });
        });
    },
  };

  function parseImages(images) {
    if (!images || images.length === 0) {
      return [];
    }

    return images.map((img, indx) => {
      return {
        uid: img.id,
        name: indx,
        status: 'done',
        url: img.path,
      };
    });
  }

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}
